/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ The Control Panel's strings. HU is primary; EN alongside it.              │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️ **ITS OWN CATALOGUE, NOT A SLICE OF THE PRODUCT'S** (`decisions.md` S-6). R-1 names *"two
 * `TranslationKey` unions"* among the things that made the outgoing project's boundary expensive —
 * and that was two unions for **one** application. Here there are genuinely two applications, and
 * the operator vocabulary (tenant, suspend, trial) has no business in a product typecheck.
 *
 * ⚠️ **THE TWO LOCALES ARE ONE TYPE, so a missing key is a TYPE ERROR.** `EN` is declared
 * `Catalogue`, derived from `HU`. A forgotten English string fails the typecheck instead of
 * rendering a Hungarian sentence to an English reader.
 *
 * ⚠️ **HUNGARIAN INFLECTS BY VOWEL HARMONY, so a suffix is never glued to a placeholder.**
 * `{name}-től` is wrong for half of all names. Every message below is phrased so the placeholder
 * lands where no suffix follows it, or the sentence is rewritten.
 *
 * ⚠️ **Rule 7 covers this application too** — including `aria-label`s, page titles, empty states
 * and every refusal an operator can provoke.
 */

export const HU = {
  'app.name': 'Forecastify',
  'app.console': 'Control Panel',

  /* ── page titles ───────────────────────────────────────────────────────────────────────── */
  'title.signIn': 'Belépés · Control Panel',
  'title.enrol': 'Hitelesítő beállítása · Control Panel',
  'title.tenants': 'Szervezetek · Control Panel',
  'title.tenant': 'Szervezet · Control Panel',
  'title.plans': 'Csomagok · Control Panel',
  'title.signedOut': 'Kiléptél · Control Panel',

  /* ── the shell ─────────────────────────────────────────────────────────────────────────── */
  'env.development': 'Fejlesztés',
  'env.production': 'Éles',
  'nav.main': 'Fő navigáció',
  'nav.group.tenants': 'Ügyfelek',
  'nav.group.operations': 'Üzemeltetés',
  'nav.group.oversight': 'Felügyelet',
  'nav.collapse': 'Oldalsáv összecsukása',
  'nav.expand': 'Oldalsáv kinyitása',
  'nav.theme': 'Téma',
  'nav.language': 'Nyelv',
  'nav.breadcrumbs': 'Hol vagy',
  'theme.light': 'Világos',
  'theme.dark': 'Sötét',
  'theme.latte': 'Latte',
  'theme.frappe': 'Frappé',
  'theme.macchiato': 'Macchiato',
  'theme.mocha': 'Mocha',
  'theme.nord': 'Nord',
  'theme.radix': 'Radix',
  'health.heading': 'Állapot most',
  'health.count': '{count} tétel vár rád',
  'health.quiet': 'Nincs teendő',
  'health.none': 'Nincs',
  'health.failed':
    'Az állapot nem töltődött be. Attól még lehet baj — nézd meg a Háttérmunka oldalt.',
  'health.overdue': 'Lemaradt ütemezett munka',
  'health.failedJobs': 'Hibás háttérfeladat',
  'health.atCeiling': 'Plafonon lévő munkaterület',
  'health.suspended': 'Felfüggesztett szervezet',
  'nav.tenants': 'Szervezetek',
  'nav.plans': 'Csomagok',
  'nav.userMenu': 'Üzemeltetői menü',
  'nav.skipToContent': 'Ugrás a tartalomra',
  'nav.signOut': 'Kijelentkezés',

  /* ── sign-in ───────────────────────────────────────────────────────────────────────────── */
  'signIn.email': 'E-mail',
  'signIn.emailPlaceholder': 'uzemelteto@loginet.hu',
  'signIn.password': 'Jelszó',
  'signIn.code': 'Kód',
  'signIn.codePlaceholder': '123456',
  /* A hint, not a placeholder: it is true before the field is touched and stays true. */
  'signIn.codeHint': 'A hitelesítő alkalmazásban látható hat számjegy.',
  'signIn.submit': 'Belépés',
  'signIn.working': 'Belépés…',
  /* ⚠️ ONE message for every refusal, mirroring the API. A screen that distinguished "hibás kód"
     from "hibás jelszó" would leak exactly what the server refuses to. */
  'signIn.refused': 'Az adatok nem stimmelnek. Próbáld újra.',
  'signIn.unreachable': 'A kiszolgáló nem válaszol.',

  /* ── enrolment ─────────────────────────────────────────────────────────────────────────── */
  'enrol.title': 'Második faktor regisztrálása',
  'enrol.lede':
    'Az operátori fiókodat egy másik operátor hozta létre. Belépni csak regisztrált hitelesítő alkalmazással tudsz, ezért ezt most egyszer beállítod.',
  'enrol.begin': 'Regisztráció indítása',
  'enrol.beginWorking': 'Titok kérése…',
  /* ⚠️ The QR code's accessible name. It says what the picture is FOR — a screen reader announcing
   * "QR code" has told the listener nothing they can act on, and the key itself is unreadable to
   * them either way, which is why it is also on the page as text. */
  'enrol.qrLabel': 'QR-kód a hitelesítő alkalmazás beállításához',
  'enrol.scanHint':
    'Olvasd be a QR-kódot a hitelesítő alkalmazással, vagy írd be a kulcsot kézzel.',
  'enrol.secret': 'Titkos kulcs',
  /* The mockup's own wording for the same fallback (`docs/mockup/markup.html`, the 2FA dialog):
   * manual entry is what you reach for when the camera will not cooperate. */
  'enrol.secretHint': 'Ha a beolvasás nem megy, írd be kézzel ezt a kulcsot.',
  'enrol.verify': 'Ellenőrzés',
  'enrol.verifyWorking': 'Véglegesítés…',
  'enrol.done': 'A második faktor aktív. Mostantól minden belépéskor kéri a rendszer.',
  'enrol.refused': 'Nem sikerült. Ellenőrizd a jelszót és a kódot.',
  'enrol.backToSignIn': 'Tovább a belépéshez',

  /* ── signed out ────────────────────────────────────────────────────────────────────────── */
  'signedOut.title': 'Kijelentkeztél',
  'signedOut.lede': 'A munkamenet ezen az eszközön lezárult.',
  'signedOut.again': 'Újra belépés',

  /* ── the tenant list ───────────────────────────────────────────────────────────────────── */
  'tenants.heading': 'Szervezetek',
  'tenants.search': 'Név vagy slug',
  'tenants.filter': 'Állapot',
  'tenants.filter.all': 'Minden állapot',
  'tenants.filter.rangeFrom': 'Ettől',
  'tenants.filter.rangeTo': 'Eddig',
  'tenants.column.name': 'Név',
  'tenants.column.status': 'Állapot',
  'tenants.column.workspaces': 'Munkaterület',
  'tenants.column.members': 'Ember',
  'tenants.column.trial': 'Próbaidő vége',
  'tenants.column.plan': 'Csomag',
  'tenants.column.seats': 'Helyek',
  /* ⚠️ Nem „MRR”: az érték a katalógus ára × a használt helyek, nem könyvelt bevétel. */
  'tenants.column.value': 'Havi érték',
  'tenants.column.created': 'Létrehozva',
  'tenants.empty': 'Nincs ilyen szervezet.',
  'tenants.more': 'Következő oldal',
  'tenants.loading': 'Betöltés…',
  'tenants.failed': 'A lista nem jött meg.',
  'tenants.retry': 'Újra',
  'tenants.count': '{count} szervezet',

  /* ── the states, in one place, because three screens name them ─────────────────────────── */
  'status.trial': 'Próbaidő',
  'status.active': 'Aktív',
  'status.suspended': 'Felfüggesztve',

  /* ── one tenant ────────────────────────────────────────────────────────────────────────── */
  'tenant.back': 'Vissza a listához',
  'tenant.slug': 'Azonosító',
  'tenant.created': 'Létrehozva',
  'tenant.workspaces': 'Munkaterület',
  'tenant.members': 'Ember',
  'tenant.trialEnds': 'Próbaidő vége',
  'tenant.suspendedAt': 'Felfüggesztve',
  'tenant.suspensionReason': 'A felfüggesztés oka',
  /* ⚠️ Named out loud, because an operator writing free text needs to know who reads it. */
  'tenant.reasonPrivate': 'Ezt csak az üzemeltetők látják.',
  'tenant.notFound': 'Nincs ilyen szervezet.',
  /* ⚠️ The boundary, on the screen. S-1: an operator sees tenants, never their contents. */
  'tenant.boundary.title': 'Kívülről látod',
  'tenant.tabs': 'A szervezet nézetei',
  'tenant.contact': 'Kapcsolattartó',
  'tenant.contact.since': 'A szervezetet {date} regisztrálta',
  'tenant.lastRequest': 'Utolsó kérés',
  'tenant.lastRequest.never': 'Még soha',
  'tenant.ago.minutes': '{count} perce',
  'tenant.ago.hours': '{count} órája',
  'tenant.ago.days': '{count} napja',
  'tenant.seats.unlimited': 'Korlátlan',
  'tenant.tab.overview': 'Áttekintés',
  'tenant.tab.plan': 'Csomag és helyek',
  'tenant.tab.features': 'Funkciók',
  'tenant.tab.audit': 'Napló',
  'tenant.boundary.visible': 'Látod',
  'tenant.boundary.hidden': 'Nem látod',
  'tenant.boundary.v1': 'Azonosító, állapot, létrehozás',
  'tenant.boundary.v2': 'Csomag, helyek, funkciók',
  'tenant.boundary.v3': 'A kapcsolattartó neve és e-mail-címe',
  'tenant.boundary.h1': 'Projektek és feladatok',
  'tenant.boundary.h2': 'Megjegyzések és csatolmányok',
  'tenant.boundary.h3': 'Munkaidő, szerződések, levelek',
  'tenant.boundary.enforced':
    'Az adatbázis zárja le: az operátori Postgres-szerep egyetlen workspace_id-t tartalmazó táblához sem kap jogot.',

  /* ── the two verbs ─────────────────────────────────────────────────────────────────────── */
  'tenant.suspend': 'Felfüggesztés',
  'tenant.suspend.title': '{name} felfüggesztése',
  'tenant.suspend.lede':
    '{people} ember azonnal kizárva mind a {workspaces} munkaterületről. Az adatok érintetlenek maradnak; aktiválással visszakapják a hozzáférést.',
  'tenant.suspend.reason': 'Indoklás',
  'tenant.suspend.reasonHint': 'Bekerül a naplóba. Legalább 10 karakter.',
  'tenant.suspend.confirm': 'Felfüggesztés',
  'tenant.suspend.working': 'Felfüggesztés…',
  'tenant.suspend.cancel': 'Mégse',
  'tenant.activate': 'Aktiválás',
  'tenant.activate.working': 'Aktiválás…',

  /* ── refusals, by `code`. Rule 5: a client branches on the code, never on the message. ─── */
  /* ── the catalogue ─────────────────────────────────────────────────────────────────────── */
  'plans.heading': 'Csomagok',
  'plans.lede': 'A csomag adja a helyek számát és azt, mely funkciókat kapja meg a szervezet.',
  'plans.column.name': 'Név',
  'plans.column.price': 'Ár / hely / hó',
  'plans.column.seats': 'Helykorlát',
  'plans.column.features': 'Funkciók',
  'plans.column.tenants': 'Szervezet',
  'plans.column.state': 'Állapot',
  'plans.empty': 'Még nincs csomag. Amíg nincs az első, minden szervezet korlát nélkül nő.',
  'plans.loading': 'Betöltés…',
  'plans.failed': 'A csomagok nem jöttek meg.',
  'plans.retry': 'Újra',
  'plans.new': 'Új csomag',
  'plans.edit': 'Szerkesztés',
  /* ⚠️ „Korlátlan”, nem üres cella: az üres cella azt jelenti, hogy nem jött meg az adat. */
  'plans.unlimited': 'Korlátlan',
  'plans.state.active': 'Kínálva',
  'plans.state.inactive': 'Visszavonva',
  'plans.tenantsOn': 'ennyi szervezet van rajta: {count}',

  'plans.form.create': 'Új csomag',
  'plans.form.edit': 'Csomag szerkesztése',
  'plans.form.name': 'Név',
  'plans.form.price': 'Ár egy helyre, egy hónapra',
  'plans.form.priceHint': 'Csak szám, legfeljebb két tizedessel.',
  'plans.form.currency': 'Pénznem',
  'plans.form.currencyHint': '3 betűs kód, például EUR vagy HUF.',
  'plans.form.seats': 'Helyek száma',
  /* ⚠️ Kimondva, mert a mező üresen hagyása értelmes válasz — és nem ugyanaz, mint a nulla. */
  'plans.form.seatsHint': 'Üresen korlátlan. 0 esetén senki nem vehető fel.',
  'plans.form.features': 'Funkciók',
  'plans.form.active': 'Kínálva',
  'plans.form.activeHint':
    'Ha visszavonod, új szervezet nem kaphatja meg, a meglévők rajta maradnak.',
  'plans.form.save': 'Mentés',
  'plans.form.saving': 'Mentés…',
  'plans.form.cancel': 'Mégse',

  /* ── a szervezet csomagja, helyei, funkciói ────────────────────────────────────────────── */
  'tenant.plan': 'Csomag',
  'tenant.plan.none': 'Nincs csomag',
  /* ⚠️ Ez a próbaidő állapota, és nem hiba — kimondva, hogy ne tűnjön elmaradt beállításnak. */
  'tenant.plan.noneHint': 'Csomag nélkül nincs helykorlát. Ez a próbaidő szokásos állapota.',
  'tenant.plan.change': 'Csomag módosítása',
  'tenant.plan.saving': 'Mentés…',
  'tenant.plan.inactive': 'kivezetett',

  'tenant.seats': 'Helyek',
  'tenant.seats.used': 'Használt',
  'tenant.seats.pending': '{count} függő meghívó',
  /* ⚠️ Kimondva: a meghívó helyet foglal, különben a szám megmagyarázhatatlan. */
  'tenant.seats.pendingHint': 'Az el nem fogadott meghívó is helyet foglal.',
  'tenant.seats.limit': 'Korlát',
  'tenant.seats.override': 'Egyedi korlát',
  'tenant.seats.overrideHint': 'Hagyd üresen, hogy a csomag korlátja érvényesüljön.',
  'tenant.seats.fromPlan': 'A csomagból',
  'tenant.seats.save': 'Korlát mentése',
  'tenant.seats.saving': 'Mentés…',
  /* ⚠️ Kimondva, mert a korlát leszállítása senkit sem zár ki — csak a növekedést állítja meg. */
  'tenant.seats.over':
    'Több hely van használatban, mint amennyit a korlát engedne. Senkit sem zárt ki: a szervezet addig nem tud nőni, amíg vissza nem áll a korlát alá.',

  'tenant.features': 'Funkciók',
  'tenant.features.lede': 'A csomag adja az alapot; itt egy szervezetre lehet eltérni tőle.',
  'tenant.features.on': 'Be',
  'tenant.features.off': 'Ki',
  'tenant.features.source.plan': 'A csomagból',
  'tenant.features.source.override': 'Egyedi felülírás',
  'tenant.features.source.default': 'Alapértelmezés',
  'tenant.features.clear': 'Felülírás törlése',
  'tenant.features.clearHint': 'Törlés után ez lesz: {value}',
  'tenant.features.saving': 'Mentés…',
  'tenant.features.failed': 'A funkciók nem jöttek meg.',

  'error.unauthenticated': 'A munkamenet véget ért. Lépj be újra.',
  'error.not_found': 'Ez a szervezet nincs meg. Lehet, hogy közben megszűnt.',
  'error.already_suspended': 'Ez a szervezet már fel van függesztve.',
  'error.already_active': 'Ez a szervezet már aktív.',
  'error.validation_failed': 'Az adatok nem megfelelőek.',
  'error.duplicate_name': 'Van már ilyen nevű csomag.',
  'error.unknown_feature': 'Ilyen funkciót egyetlen verzió sem ismer.',
  'error.unknown_plan': 'Ez a csomag nincs meg.',
  'error.already_default': 'Ennél a szervezetnél nincs egyedi beállítás, amit törölni lehetne.',
  'error.conflict': 'Közben más is módosította. Töltsd újra.',
  'error.unknown': 'Valami félrement. Próbáld újra.',
  'error.unreachable': 'A kiszolgáló nem válaszol.',

  /* ── S7: az üzemeltetési napló és a job-monitor ─────────────────────────────────────────── */

  'title.operations': 'Üzemeltetés · Control Panel',
  'title.audit': 'Napló · Control Panel',
  'nav.operations': 'Háttérmunka',
  'nav.audit': 'Napló',

  /* ── C27.5 · mire megy el az Agent kerete ──────────────────────────────────────────────────
     ⚠️⚠️ **EZ A KÉPERNYŐ NEM TUD BESZÉLGETÉST MEGJELENÍTENI, ÉS NEM AZÉRT, MERT NEM AKAR.** A
     `0047` `security definer` függvénye csak számokat, neveket és időpontokat ad vissza — egy
     feladatcím vagy egy kérdés nem tud ide eljutni, mert nincs olyan mező. S-1 határa. */
  'title.spend': 'Agent · Control Panel',
  'nav.spend': 'Agent-használat',
  'spend.heading': 'Az Agent használata',
  'spend.lede':
    'Mennyi tokent használt az Agent, workspace-enként. Csak számok: a beszélgetések tartalma ide nem jut el, és nem is tud.',
  'spend.window.label': 'Időszak',
  'spend.window.today': 'Ma',
  'spend.window.week': '7 nap',
  'spend.window.month': '30 nap',
  'spend.window.quarter': '90 nap',
  'spend.loading': 'Betöltés…',
  'spend.failed': 'Az agent-használat nem töltődött be.',
  'spend.retry': 'Újra',
  'spend.empty': 'Ebben az időszakban senki nem kérdezte az agentet.',
  /* ⚠️ Vowel harmony: a szám a tagmondat végén áll, ragozás nélkül. */
  'spend.cap': 'Napi keret workspace-enként: {cap} token. Az adatok kezdete: {since}.',
  'spend.noCap': 'Nincs napi plafon beállítva.',
  'spend.column.tenant': 'Szervezet / munkaterület',
  'spend.column.workspace': 'Workspace',
  'spend.column.questions': 'Kérdés',
  'spend.column.calls': 'API-hívás',
  /* ⚠️ Ez az, amit a keret számol: input + output. A cache külön oszlop, mert egy tizedébe kerül. */
  'spend.column.billed': 'Számlázott token',
  'spend.column.cached': 'Cache-ből',
  'spend.column.where': 'Hol futott',
  'spend.column.last': 'Utolsó kérdés',

  /*
   * ⚠️⚠️ **NINCS TOKENNÉV EGYETLEN KULCSBAN SEM, ÉS EZ DÖNTÉS.** Egy személyes hozzáférési kulcs
   * címkéjét a tenant írta — *"Bence laptopja"* —, és az a tenant saját szövege. S-1 határa, ugyanaz,
   * ami a `mail.send` tartalmát is kint tartja ebből a konzolból: a hitelesítő adat itt csak
   * darabszámként jelenik meg.
   *
   * ⚠️ Az *"írás"* oszlop azt számolja, amit egy ember jóváhagyott: R-25 kapuja miatt egy MCP-kliens
   * önmagában semmit nem írhat. A nulla ott nem hiba, hanem a működés.
   */
  /* ⚠️ Jóváhagyott javaslat, nem *"amit az MCP írt"* — utóbbi olyasmit állítana, ami nem történhet. */

  'audit.heading': 'Üzemeltetői napló',
  'audit.lede':
    'Minden művelet, amit a Control Panelből vagy az óra végzett — ki, mit, mikor, és mi változott.',
  'audit.loading': 'Napló betöltése…',
  'audit.failed': 'A napló nem töltődött be.',
  'audit.retry': 'Újra',
  'audit.empty': 'Még egyetlen művelet sem történt ezen a telepítésen.',
  'audit.emptyFiltered': 'Ezzel a szűréssel nincs bejegyzés.',
  'audit.more': 'Régebbiek',
  'audit.noDiff': 'Nincs rögzített változás.',
  'audit.removed': 'törölve',
  'audit.filter.resource': 'Érintett elem szerint',
  'audit.filter.action': 'Művelet szerint',
  'audit.filter.allResources': 'Minden erőforrás',
  'audit.filter.allActions': 'Minden művelet',
  'audit.column.when': 'Időpont',
  'audit.column.actor': 'Ki',
  'audit.column.action': 'Művelet',
  'audit.column.resource': 'Erőforrás',
  'audit.column.change': 'Változás',
  'audit.actor.system': 'automatikus',
  'audit.actor.systemBadge': 'automatikus',
  'audit.actor.unknown': 'Ismeretlen üzemeltető',
  'audit.action.tenantSuspend': 'Szervezet felfüggesztve',
  'audit.action.tenantActivate': 'Szervezet aktiválva',
  'audit.action.tenantPlanAssign': 'Csomag hozzárendelve',
  'audit.action.tenantSeatLimitSet': 'Férőhelykorlát módosítva',
  'audit.action.tenantFlagSet': 'Funkció felülírva',
  'audit.action.tenantFlagClear': 'Funkció-felülírás törölve',
  'audit.action.planCreate': 'Csomag létrehozva',
  'audit.action.planUpdate': 'Csomag módosítva',
  'audit.action.planDeactivate': 'Csomag visszavonva',
  'audit.action.jobRequeue': 'Feladat újra sorba állítva',
  'audit.action.choreRun': 'Ütemezett munka kézzel indítva',
  'audit.resource.tenant': 'Szervezet',
  'audit.resource.plan': 'Csomag',
  'audit.resource.job': 'Feladat',
  'audit.resource.chore': 'Ütemezett munka',

  'ops.payloadNote':
    'A feladatok tartalmát, például egy kiküldött levél címzettjét és szövegét, nem látod. Újrafuttathatod, elolvasni nem tudod.',
  'ops.jobsEmpty':
    'Nincs ilyen feladat. Ha valami elakad, itt jelenik meg, és innen küldheted vissza a sorba.',
  'ops.chores.runBody':
    'Éles adatokon fut, és e-mailt küldhet ki ügyfeleknek. Az ütemezést nem módosítja: a következő futás a szokott időben jön.',
  'enrol.step.begin': 'Kezdés',
  'enrol.step.key': 'Kulcs',
  'enrol.step.verify': 'Ellenőrzés',
  'enrol.step.done': 'Kész',
  'enrol.steps': 'A regisztráció lépései',
  'ops.heading': 'Üzemeltetés',
  'ops.lede':
    'Az óra által indított munkák és a feladatsor. Ami itt hibás, arról máshol nem érkezik jelzés.',
  'ops.loading': 'Betöltés…',
  'ops.failed': 'Az adatok nem töltődtek be.',
  'ops.retry': 'Újra',
  'ops.never': 'Még nem futott',
  'ops.cadence.minutes': '{n} percenként',
  'ops.cadence.hours': '{n} óránként',
  'ops.cadence.days': '{n} naponta',
  'ops.chores.heading': 'Ütemezett munka',
  'ops.chores.lede':
    'Amit az óra indít, nem egy kérés. Mindegyik ismételhető, így kézzel is elindítható.',
  'ops.chores.column.chore': 'Munka',
  'ops.chores.column.cadence': 'Ütem',
  'ops.chores.column.lastRun': 'Utolsó futás',
  'ops.chores.column.state': 'Állapot',
  'ops.chores.column.outcome': 'Eredmény',
  'ops.chores.column.day': 'Hiba / 24 óra',
  'ops.chores.run': 'Futtatás most',
  'ops.chores.running': 'Fut…',
  'ops.chores.overdue': 'Két ütemet is kihagyott.',
  'ops.chores.nothingToDo': 'Nem volt tennivaló.',
  'ops.chores.confirm':
    'Elindítod ezt a munkát most? Éles adatokon fut — levelet küldhet vagy szervezetet függeszthet fel.',
  'ops.chore.running': 'Fut',
  'ops.chore.succeeded': 'Rendben',
  'ops.chore.failed': 'Hibára futott',
  'ops.queue.heading': 'Feladatsor',
  'ops.queue.lede': 'Amit egy kérés állított be, típus szerint összesítve.',
  'ops.queue.column.kind': 'Típus',
  'ops.queue.column.pending': 'Várakozik',
  'ops.queue.column.running': 'Fut',
  'ops.queue.column.done': 'Kész',
  'ops.queue.column.failed': 'Hibás',
  'ops.queue.column.oldest': 'Legrégebbi várakozó',
  'ops.queue.unhandled': 'nincs feldolgozó',
  'ops.jobs.heading': 'Háttérfeladatok',
  'ops.jobs.lede': 'Ami elhasználta a próbálkozásait, és amit egy leállt feldolgozó fog.',
  'ops.jobs.empty': 'Egyetlen hibás feladat sincs. A sor tiszta.',
  'ops.jobs.column.kind': 'Típus',
  'ops.jobs.column.tenant': 'Szervezet',
  'ops.jobs.column.attempts': 'Próbálkozás',
  'ops.jobs.column.error': 'Hiba',
  'ops.jobs.column.when': 'Létrejött',
  'ops.jobs.requeue': 'Vissza a sorba',
  'ops.jobs.requeueing': 'Küldés…',
  'ops.jobs.more': 'Régebbiek',
  'ops.jobs.stalled': 'Megszorult',
  'ops.jobs.noTenant': 'Nincs szervezet',
  'ops.jobs.confirm':
    'Újra sorba állítod? A levél valóban kimegy, és a próbálkozások száma nullázódik.',
  'ops.error.jobNotFailed': 'Csak hibás feladat állítható újra sorba. Töltsd újra a listát.',
  'ops.error.unknownChore': 'Ilyen ütemezett munkát egyetlen verzió sem ismer.',

  'tenant.audit': 'Napló',
  'tenant.auditLede': 'Legutóbbi operátori műveletek',
  'tenant.auditEmpty': 'Ezen a szervezeten még nem végzett műveletet senki.',
  'tenant.auditAll': 'Teljes napló',
  'tenant.auditFailed': 'A napló nem töltődött be.',

  /* ── C9 · the danger tab and the deletion ────────────────────────────────── */
  'tenant.tab.danger': 'Végleges törlés',
  'danger.tier': 'Visszafordíthatatlan',
  'danger.title': 'A szervezet végleges törlése',
  'danger.body':
    'A szervezet és minden, ami alatta van, véglegesen törlődik. Felfüggesztés nem kell előtte, ezért a megerősítés több lépésből áll, és a számok láttán dől el.',
  'danger.prepare': 'Törlés előkészítése',
  'danger.status': 'Állapot',
  'danger.monthly': 'Havi érték',

  'title.tenantDelete': 'Végleges törlés · Control Panel',
  'delete.back': 'Vissza a szervezethez',
  'delete.title': '{name} végleges törlése',
  'delete.lede': 'Nem vonható vissza, és a Control Panelből nem állítható vissza.',
  'delete.step1': 'Mit töröl',
  'delete.step2': 'Tudomásul veszed',
  'delete.step3': 'Megerősítés',
  'delete.live': 'Élő ügyfél. Nincs felfüggesztve: a tagjai most is használják.',
  'delete.livePaying':
    'Élő, fizető ügyfél, havi {amount}. Nincs felfüggesztve: a tagjai most is használják.',
  'delete.suspendInstead': 'Felfüggesztés helyette',
  'delete.ack.workspaces':
    '{count} munkaterület törlődik, a bennük lévő összes projekttel, feladattal, munkaidővel és szerződéssel együtt.',
  'delete.ack.people': '{count} ember azonnal elveszíti a hozzáférését.',
  'delete.ack.peopleLive':
    '{count} ember azonnal elveszíti a hozzáférését, akkor is, ha éppen dolgozik benne.',
  'delete.ack.paying':
    'Fizető ügyfél, havi {amount}. A törlés nem mondja fel a szerződést és nem rendezi a számlázást.',
  'delete.ack.undo': 'Tudom, hogy ez nem vonható vissza.',
  'delete.slugLabel': 'Írd be a slugot: {slug}',
  'delete.slugHint': 'Beillesztés itt nem működik; gépeld be.',
  'delete.reason': 'Indoklás',
  'delete.reasonHint': 'Bekerül a naplóba. Legalább 10 karakter.',
  'delete.missing': 'Még hiányzik:',
  'delete.missing.acks': 'tudomásulvétel',
  'delete.missing.slug': 'slug',
  'delete.missing.reason': 'indoklás',
  'delete.ready': 'Minden feltétel teljesül.',
  'delete.confirm': 'Szervezet végleges törlése',
  'delete.working': 'Törlés…',
  'delete.cancel': 'Mégsem',
  'delete.why.title': 'Miért ennyi lépés?',
  'delete.why.body':
    'A slug kimásolható a képernyőről, ezért a begépelése egymagában kevés. A tudomásulvétel azt igazolja, hogy a számokat is láttad, az indoklás pedig az egyetlen, ami fél év múlva megmarad arról, hogy miért.',
  'delete.error.slug': 'Ez nem ennek a szervezetnek a slugja.',

  'delete.done.kicker': 'Szervezet véglegesen törölve',
  'delete.done.title': '{name} törölve',
  'delete.done.body':
    'A szervezet és minden adata eltávolítva. A napló megőrzi, ki, mikor és miért törölte.',
  'delete.done.removed': 'Eltávolítva',
  'delete.done.removedValue': '{workspaces} munkaterület, {people} ember',
  'delete.done.by': 'Törölte',
  'delete.done.at': 'Időpont',
  'delete.done.reason': 'Indoklás',
  'delete.done.audit': 'Naplóbejegyzés',
  'delete.done.toTenants': 'Vissza a szervezetekhez',
  'delete.done.openAudit': 'Megnyitás a naplóban',

  /* ── C8 · the operator roster ────────────────────────────────────────── */
  'title.operators': 'Üzemeltetők · Control Panel',
  'nav.operators': 'Operátorok',
  'operators.heading': 'Operátorok',
  'operators.lede':
    'Az operátorok egymást kezelik. Belépni csak regisztrált második faktorral lehet, ezért egy meghívott addig „Regisztrációra vár”, amíg ezt meg nem teszi.',
  'operators.loading': 'Betöltés…',
  'operators.failed': 'Az üzemeltetők listája nem töltődött be.',
  'operators.you': 'te',
  'operators.never': 'Még nem lépett be',
  'operators.working': 'Folyamatban…',
  'operators.cancel': 'Mégse',
  'operators.column.person': 'Név',
  'operators.column.state': 'Állapot',
  'operators.column.lastSeen': 'Utolsó belépés',
  'operators.column.since': 'Felvéve',
  'operators.column.actions': 'Műveletek',
  'operators.state.active': 'Aktív',
  'operators.state.invited': 'Regisztrációra vár',
  'operators.state.invitedHint': 'Még nem állított be hitelesítőt, így belépni sem tud.',
  'operators.state.suspended': 'Felfüggesztve',
  'operators.invite': 'Operátor meghívása',
  'operators.invite.title': 'Operátor meghívása',
  'operators.invite.lede':
    'A meghívott a linken jelszót állít be és regisztrálja a második faktort; addig nem tud belépni.',
  'operators.invite.email': 'E-mail',
  'operators.invite.name': 'Név',
  'operators.invite.whatHappens':
    'Meghívó linket küldünk neki. A linken jelszót választ, beolvassa a kulcsot egy hitelesítő alkalmazással, és az első kóddal befejezi a regisztrációt — utána rögtön be is lép. A link 3 napig érvényes.',
  'operators.invite.confirm': 'Meghívás küldése',
  'operators.invite.working': 'Küldés…',
  'operators.action.reset': 'Jelszó-visszaállítás küldése',
  'operators.action.resend': 'Meghívó újraküldése',
  'operators.action.suspend': 'Felfüggesztés',
  'operators.action.activate': 'Újraaktiválás',
  'operators.action.delete': 'Törlés',
  'operators.suspend.title': '{who} felfüggesztése',
  'operators.suspend.lede':
    'Azonnal kijelentkezteti, és nem tud belépni, amíg újra nem aktiválod. A fiókja és a naplóbejegyzései megmaradnak.',
  'operators.suspend.confirm': 'Felfüggesztés',
  'operators.delete.title': '{who} törlése',
  'operators.delete.lede': 'A fiók véglegesen megszűnik. A naplóbejegyzései megmaradnak a nevével.',
  'operators.delete.confirm': 'Törlés',
  'operators.reset.title': 'Jelszó-visszaállító küldése',
  'operators.reset.lede':
    'Levelet küldünk, amivel a kijelölt személy ({who}) új jelszót állíthat be. A hitelesítő alkalmazását ez nem érinti.',
  'operators.reset.confirm': 'Elküldöm',
  'operators.done.invited': 'Operátor meghívva',
  'operators.done.suspended': 'Operátor felfüggesztve',
  'operators.done.activated': 'Operátor újraaktiválva',
  'operators.done.deleted': 'Operátor törölve',
  'operators.done.reset': 'Jelszó-visszaállítás elküldve',
  'operators.error.thatIsYou': 'A saját fiókodon ezt nem végezheted el.',
  'operators.error.lastOperator':
    'Ő az utolsó üzemeltető, aki be tud lépni. Előbb hívj meg valaki mást.',
  'operators.error.alreadyAnOperator': 'Ez a cím már üzemeltetőhöz tartozik.',
  'operators.error.belongsToAMember':
    'Ez a cím egy munkaterület dolgozójáé. Egy cím nem lehet egyszerre a kettő.',
  'audit.action.tenantDelete': 'Szervezet törölve',
  'audit.action.workspaceDeleted': 'Workspace véglegesen törölve',
  'audit.action.operatorInvite': 'Üzemeltető meghívva',
  'audit.action.operatorSuspend': 'Üzemeltető felfüggesztve',
  'audit.action.operatorActivate': 'Üzemeltető visszaállítva',
  'audit.action.operatorDelete': 'Üzemeltető eltávolítva',
  'audit.action.operatorPasswordReset': 'Jelszó-visszaállító küldve',
  'audit.resource.operator': 'Üzemeltető',
  'audit.resource.workspace': 'Workspace',

  /* ── C10 · an organisation is invited ─────────────────────────────────── */
  'status.awaitingSignup': 'Meghívva',
  /* `0202` · the admin accepted and has not created the workspace yet. */
  'status.awaitingSetup': 'Beállításra vár',
  'tenants.invite': 'Szervezet meghívása',
  'tenants.invite.title': 'Szervezet meghívása',
  'tenants.invite.lede':
    'Az admin e-mailben kap egy regisztrációs linket. A szervezet már most létrejön, és a regisztrációig erre vár.',
  'tenants.invite.name': 'Szervezet neve',
  'tenants.invite.nameHint': 'Ebből készül az azonosítója és az első munkaterülete is.',
  'tenants.invite.adminName': 'Admin neve',
  'tenants.invite.adminEmail': 'Admin e-mail-címe',
  'tenants.invite.emailHint':
    'A meghívó csak ezzel a címmel fogadható el. Más címmel regisztrálva nem működik.',
  'tenants.invite.confirm': 'Meghívó küldése',
  'tenants.invite.working': 'Küldés…',
  'tenants.invite.cancel': 'Mégse',
  'tenants.invite.done': 'A meghívó elindult ide: {email}',
  'tenants.invite.error.unsluggable':
    'Ebből a névből nem készíthető azonosító. Legalább három betű vagy szám kell bele.',
  'tenants.invite.error.alreadyMember':
    'Ez a cím már egy munkaterület dolgozójáé. Egy cím csak egy szervezetet indíthat.',
  'tenants.invite.error.isAnOperator': 'Ez a cím egy üzemeltetőhöz tartozik.',

  'tenant.pending.title': 'Meghívva',
  'tenant.pending.lede':
    'A szervezet létrejött, de még senki sem fogadta el a meghívót. Az első munkaterülete készen áll, dolgozója még nincs.',
  'tenant.pending.address': 'Meghívva:',
  'tenant.pending.expires': 'A link lejár:',
  'tenant.pending.expired': 'A link lejárt:',
  'tenant.pending.resend': 'Meghívó újraküldése',
  'tenant.pending.working': 'Küldés…',
  'tenant.pending.resendHint': 'Az újraküldés új linket ad; a régi azonnal érvénytelen lesz.',
  'tenant.pending.sent': 'Meghívó újraküldve',
  'tenant.pending.failed': 'A meghívót nem sikerült újraküldeni.',
  'audit.action.tenantInvite': 'Szervezet meghívva',
  'audit.action.tenantInviteResend': 'Meghívó újraküldve',
  'tenants.from': 'Létrehozva ettől',
  'tenants.to': 'eddig',
  'tenants.clearFilters': 'Szűrők törlése',
  'tenants.emptyFilter': 'A szűrők minden szervezetet elrejtettek.',
  'tenants.withdrawn': 'visszavonva',
  'tenants.invite.emailPlaceholder': 'nev@ceg.hu',
  'tenants.invite.plan': 'Csomag',
  'tenants.invite.seats': 'Helyek',
  'tenants.invite.seatsFromPlan': 'Üresen a csomag korlátja érvényes: {limit}.',
  'tenants.invite.seatsOverride': 'Egyedi korlát: {count} hely.',
  'tenants.invite.unlimited': 'korlátlan',
  'tenants.invite.done.title': 'Meghívó elküldve',
  'pager.page': '{n}. oldal',
  'pager.previous': 'Előző oldal',
  'pager.next': 'Következő oldal',
  'pager.jump': 'Ugrás oldalra',
  'cal.today': 'Ma',
  'cal.clear': 'Törlés',
  'cal.previous': 'Előző',
  'cal.next': 'Következő',
  'health.missed': '{count} ütem kimaradt',
  'toast.region': 'Értesítések',
  'toast.dismiss': 'Bezárás',
  'stepper.decrement': 'Csökkentés',
  'stepper.increment': 'Növelés',
  'tenant.createdOn': 'Létrehozva: {date}',
  'tenant.planLine': '{plan} · {price} / hely / hó',
  'tenant.suspendedSince': 'Felfüggesztve {date} óta. Minden tag ki van zárva.',
  'tenant.contact.none': 'Ennek a szervezetnek még nincs kapcsolattartója.',
  'tenant.contact.resetSent': 'Jelszó-visszaállítás elküldve',
  'tenant.suspend.tier': 'Kizárja a tagokat',
  'tenant.suspend.short': 'Még {count} karakter',
  'tenant.suspend.done': 'Szervezet felfüggesztve',
  'tenant.activate.title': '{name} aktiválása',
  'tenant.activate.lede':
    'A tagok a következő kérésüktől újra dolgozhatnak. Az adatokhoz a felfüggesztés alatt sem nyúlt senki.',
  'tenant.activate.done': 'Szervezet aktiválva',
  'tenant.seats.overShort': 'A korlát fölött',
  'tenant.seats.fromOverride': 'Egyedi felülírás',
  'tenant.trialUntil': 'Próbaidő eddig: {date}',
  'tenant.notBilling': 'Felfüggesztve, nem számláz',
  'tenant.overrides': 'Felülírt funkciók',
  'tenant.overrides.none': 'Nincs; minden a csomagból vagy alapértelmezésből jön.',
  'feature.on': 'Be',
  'feature.off': 'Ki',
  'when.today': 'ma',
  'when.yesterday': 'tegnap',
  'tenant.beyond.title': 'Projektek, feladatok, munkaidő',
  'tenant.beyond.sub': 'A határon túl. Ezt a számot innen senki nem látja.',
  'tenant.contact.never': 'még nem lépett be',
  'tenant.contact.lastSeen': 'utoljára belépett: {when}',
  'tenant.contact.reset': 'Jelszó-visszaállítás küldése',
  'tenant.pending.expiresOn': 'A link lejár: {date}',
  'tenant.pending.expiredOn': 'A link lejárt: {date}',
  'tenant.pending.setupTitle': 'Beállításra vár',
  'tenant.pending.acceptedOn':
    'Elfogadta: {date} — a workspace még nincs létrehozva. Az újraküldött link bejelentkezésre és a beállítás folytatására viszi.',
  'tenant.adjust': 'Beállítás',
  'tenant.plan.effect': 'Új havi érték: {value} · korlát: {limit} · funkciók: {features}',
  'tenant.plan.limitOverride': '{count} (egyedi)',
  'tenant.plan.current': '{price} / hely / hó · {features}',
  'tenant.save': 'Mentés',
  'tenant.cancel': 'Mégse',
  'tenant.plan.withdrawnNote':
    'Ez a csomag már nem választható új szervezetnek; ez a szervezet rajta maradhat.',
  'tenant.seats.overCount':
    '{used} hely foglalt, a korlát {limit}. Új tag nem vehető fel, amíg nem emeled a korlátot, vagy nem szabadul fel {free} hely. Senkit sem zártunk ki.',
  'tenant.seats.pendingAfter':
    '{count} függő meghívó. Ha mind elfogadják, {after} hely lesz foglalt.',
  'tenant.seats.pendingBeyond':
    '{count} függő meghívó. Ha mind elfogadják, {after} hely lesz foglalt, ez a korlát fölött van; a korláton túli meghívók nem fogadhatók el.',
  'tenant.seats.hint':
    'Üresen a csomag korlátja érvényes ({limit}). 0 esetén senki nem vehető fel.',
  'tenant.seats.clear': 'Felülírás törlése',
  'tenant.features.column.name': 'Funkció',
  'tenant.features.column.value': 'Érték',
  'tenant.features.column.source': 'Forrás',
  'tenant.features.after': 'Törlés után: {value}',
  'feature.reports': 'Riportok',
  'feature.lineItemPricing': 'Tételes árazás',
  'feature.workFreeRevenue': 'Munka nélküli bevétel',
  'feature.agent': 'Agent',
  'feature.mcp': 'MCP-hozzáférés',
  'tenant.boundary.body':
    'Operátorként a szervezet keretét kezeled: csomagot, helyeket, funkciókat és állapotot. Ami a munkaterületeken belül van, az az ügyfélé.',
  'tenant.boundary.v4': 'Forgalmi összesítők',
  'tenant.boundary.h4': 'Háttérfeladatok tartalma',
  'audit.action.tenantContactReset': 'Jelszó-visszaállítás a kapcsolattartónak',
  'plans.created': 'Csomag létrehozva',
  'plans.saved': 'Csomag mentve',
  'plans.form.nameRequired': 'Adj nevet a csomagnak.',
  'plans.form.nobody': 'Senki nem vehető fel',
  'plans.form.seatsCount': '{count} hely',
  'plans.form.withdrawWarn': '{count} szervezet rajta marad.',
  'ops.chores.late': '{count} lemaradt',
  'ops.chores.onCadence': 'Minden a szokott ütemben fut',
  'ops.chores.lateShort': 'Lemaradt',
  'ops.chores.lateMissed': 'Lemaradt · {count} ütem kimaradt',
  'ops.chores.started': 'Ütemezett munka elindítva',
  'ops.chores.runTitle': '{name}: futtatás most',
  'ops.chores.tier': 'Éles adatokon',
  'ops.chores.runConfirm': 'Futtatás',
  'ops.cadence.hourly': 'Óránként',
  'ops.cadence.daily': 'Naponta',
  'ops.jobs.summary': '{count} hibás, újraküldésre vár',
  'ops.jobs.summaryNone': 'Nincs hibás feladat',
  'ops.jobs.filter': 'Állapot szerint',
  'ops.jobs.filter.all': 'Minden állapot',
  'ops.jobs.state.failed': 'Hibás',
  'ops.jobs.state.pending': 'Sorban',
  'ops.jobs.state.running': 'Fut',
  'ops.jobs.state.done': 'Kész',
  'ops.jobs.column.state': 'Állapot',
  'ops.jobs.requeued': 'Feladat visszaküldve a sorba',
  'ops.jobs.requeueTitle': 'Feladat visszaküldése a sorba',
  'ops.jobs.tier': 'Valódi levelet küldhet',
  'ops.jobs.count': '{count} feladat',
  'chore.alertScan': 'Projektkockázatok átvizsgálása',
  'chore.dueSweep': 'Határidő-emlékeztetők kiküldése',
  'chore.dueSweepPrune': 'Emlékeztető-napló tisztítása',
  'chore.leaveCarryOver': 'Szabadság áthozása az új évre',
  'chore.trialExpiry': 'Próbaidők lejáratának ellenőrzése',
  'chore.integrationOverwatch': 'Tracker-egyeztetés',
  'chore.integrationOutbound': 'Kimenő tracker-módosítások',
  'chore.integrationPoll': 'Tracker-változások lekérdezése',
  'chore.blobSweep': 'Árva fájlok törlése',
  'spend.window': 'Időablak',
  'spend.window.7': '7 nap',
  'spend.window.30': '30 nap',
  'spend.window.90': '90 nap',
  'spend.note':
    'Napi plafon munkaterületenként: {cap} számlázott token. Plafonon az agent a következő UTC-napig nem válaszol.',
  'spend.column.tokens': 'Számlázott token',
  'spend.column.ceiling': 'Mai plafon',
  'spend.atCeiling': 'Plafonon',
  'audit.filter.clearResource': 'Szűrő törlése',
  'audit.count': '{count} bejegyzés',
  'operators.column.factor': 'Második faktor',
  'operators.factor.enrolled': 'Regisztrálva',
  'operators.factor.enrolledOn': 'Regisztrálva {date}',
  'operators.factor.none': 'Nincs regisztrálva',
  'operators.selfNote': 'Magadat nem függesztheted fel és nem törölheted.',
  'operators.done.resent': 'Meghívó újraküldve',
  'operators.invite.emailPlaceholder': 'nev@loginet.hu',
  'operators.suspend.tier': 'Kizárja',
  'operators.delete.type': 'Írd be az e-mail-címét: {email}',
  'signIn.continue': 'Tovább',
  'signIn.codeShape': 'A kód 6 számjegyből áll.',
  'signIn.back': 'Vissza',
  'enrol.copy': 'Másolás',
  'enrol.copied': 'Kimásolva',
  'enrol.secretNote':
    'A kulcs csak most látszik. Ha elveszíted a telefonod, a faktort az üzemeltetés tudja visszaállítani parancssorból.',
  'enrol.verifyLede': 'Írd be az alkalmazásban most látható 6 jegyű kódot.',
  'enrol.doneTitle': 'Regisztrálva',
  'dialog.close': 'Bezárás',
  'title.join': 'Regisztráció · Control Panel',
  'join.title': 'Csatlakozás a Control Panelhez',
  'join.lede':
    'Meghívtak a Forecastify Control Panel operátorai közé. Válassz jelszót, majd állíts be egy hitelesítő alkalmazást — a regisztráció az első kóddal fejeződik be.',
  'join.as': 'Meghívott cím',
  'join.step.password': 'Jelszó',
  'join.password': 'Jelszó',
  'join.confirm': 'Jelszó még egyszer',
  'join.rules': 'A jelszó szabályai',
  'join.rule.length': 'Legalább {min} karakter',
  'join.rule.letters': 'Kis- és nagybetű is',
  'join.rule.digits': 'Legalább egy számjegy',
  'join.rule.match': 'A két jelszó egyezik',
  'join.begin': 'Tovább a kulcshoz',
  'join.beginWorking': 'Mentés…',
  'join.weak': 'Ez a jelszó nem felel meg a szabályoknak.',
  'join.finish': 'Regisztráció befejezése',
  'join.finishWorking': 'Befejezés…',
  'join.wrongCode': 'Ez a kód nem jó. Írd be azt, amit az alkalmazás most mutat.',
  'join.loading': 'Meghívó betöltése…',
  'join.dead.title': 'Ez a meghívó már nem érvényes',
  'join.dead.lede':
    'Lejárt, már felhasználták, vagy újat küldtek helyette. Kérj új meghívót egy másik operátortól.',
  'join.welcome': 'Üdv a Control Panelben',
  'join.welcomeBody': 'A második faktor be van állítva; minden belépéskor kérni fogja a rendszer.',
  'audit.action.operatorInviteResend': 'Operátor-meghívó újraküldve',
  'auth.label': 'CONTROL PANEL',
  'auth.footer.company': 'LogiNet Systems Kft.',
  'signIn.loggingInAs': 'Belépés mint',
  'signIn.changeEmail': 'Módosítás',
  'chore.emailAvatars': 'E-mailes profilképek frissítése',
  'chore.requestMail': 'Szabadság- és túlóra-értesítők',
  'chore.contractWatch': 'Szerződésfigyelő',
  'chore.alertDigest': 'Napi projektjelzések',
  'chore.inviteReminder': 'Meghívó-emlékeztető',
  'chore.loggingMail': 'Időrögzítési emlékeztetők',
  'chore.financeMail': 'Számlázható tételek',
  'chore.integrationMail': 'Szinkronhiba-értesítő',
} as const;

export type Catalogue = Record<keyof typeof HU, string>;
export type MessageKey = keyof typeof HU;

export const EN: Catalogue = {
  'app.name': 'Forecastify',
  'app.console': 'Control Panel',

  'title.signIn': 'Sign in · Control Panel',
  'title.enrol': 'Set up authenticator · Control Panel',
  'title.tenants': 'Organisations · Control Panel',
  'title.tenant': 'Organisation · Control Panel',
  'title.plans': 'Plans · Control Panel',
  'title.signedOut': 'Signed out · Control Panel',

  'env.development': 'Development',
  'env.production': 'Production',
  'nav.group.tenants': 'Tenants',
  'nav.group.operations': 'Operations',
  'nav.group.oversight': 'Oversight',
  'nav.collapse': 'Collapse sidebar',
  'nav.expand': 'Expand sidebar',
  'nav.theme': 'Theme',
  'nav.language': 'Language',
  'nav.breadcrumbs': 'Breadcrumb',
  'theme.light': 'Light',
  'theme.dark': 'Dark',
  'theme.latte': 'Latte',
  'theme.frappe': 'Frappé',
  'theme.macchiato': 'Macchiato',
  'theme.mocha': 'Mocha',
  'theme.nord': 'Nord',
  'theme.radix': 'Radix',
  'health.heading': 'Right now',
  'health.count': '{count} items need you',
  'health.quiet': 'Nothing needs attention',
  'health.none': 'None',
  'health.failed': 'The status did not load. Something may still be wrong — check Background work.',
  'health.overdue': 'Overdue scheduled work',
  'health.failedJobs': 'Failed background jobs',
  'health.atCeiling': 'Workspaces at the ceiling',
  'health.suspended': 'Suspended organisations',
  'nav.main': 'Main navigation',
  'nav.tenants': 'Organisations',
  'nav.plans': 'Plans',
  'nav.userMenu': 'Operator menu',
  'nav.skipToContent': 'Skip to content',
  'nav.signOut': 'Sign out',

  'signIn.email': 'Email',
  'signIn.emailPlaceholder': 'operator@loginet.hu',
  'signIn.password': 'Password',
  'signIn.code': 'Code',
  'signIn.codePlaceholder': '123456',
  'signIn.codeHint': 'The six digits your authenticator app is showing.',
  'signIn.submit': 'Sign in',
  'signIn.working': 'Signing in…',
  'signIn.refused': 'Those details do not match. Try again.',
  'signIn.unreachable': 'The server is not responding.',

  'enrol.title': 'Enrol your second factor',
  'enrol.lede':
    'Your operator account was created by another operator. You can only sign in with an enrolled authenticator app, so you set it up once now.',
  'enrol.begin': 'Begin enrolment',
  'enrol.beginWorking': 'Getting the secret…',
  'enrol.qrLabel': 'QR code for setting up your authenticator app',
  'enrol.scanHint': 'Scan the QR code with your authenticator app, or type the key by hand.',
  'enrol.secret': 'Secret key',
  'enrol.secretHint': 'If scanning does not work, type this key in by hand.',
  'enrol.verify': 'Verify',
  'enrol.verifyWorking': 'Finishing…',
  'enrol.done': 'Your second factor is active. Every sign-in will ask for it.',
  'enrol.refused': 'That did not work. Check the password and the code.',
  'enrol.backToSignIn': 'Go to sign in',

  'signedOut.title': 'You are signed out',
  'signedOut.lede': 'The session on this device is closed.',
  'signedOut.again': 'Sign in again',

  'tenants.heading': 'Organisations',
  'tenants.search': 'Name or slug',
  'tenants.filter': 'Status',
  'tenants.filter.all': 'All states',
  'tenants.filter.rangeFrom': 'From',
  'tenants.filter.rangeTo': 'To',
  'tenants.column.name': 'Name',
  'tenants.column.status': 'Status',
  'tenants.column.workspaces': 'Workspaces',
  'tenants.column.members': 'People',
  'tenants.column.trial': 'Trial ends',
  'tenants.column.plan': 'Plan',
  'tenants.column.seats': 'Seats',
  /* ⚠️ Not "MRR": the figure is the catalogue price times the seats in use, not booked revenue. */
  'tenants.column.value': 'Monthly value',
  'tenants.column.created': 'Created',
  'tenants.empty': 'No organisation matches.',
  'tenants.more': 'Next page',
  'tenants.loading': 'Loading…',
  'tenants.failed': 'The list did not arrive.',
  'tenants.retry': 'Retry',
  'tenants.count': '{count} organisations',

  'status.trial': 'Trial',
  'status.active': 'Active',
  'status.suspended': 'Suspended',

  'tenant.back': 'Back to the list',
  'tenant.slug': 'Slug',
  'tenant.created': 'Created',
  'tenant.workspaces': 'Workspaces',
  'tenant.members': 'People',
  'tenant.trialEnds': 'Trial ends',
  'tenant.suspendedAt': 'Suspended',
  'tenant.suspensionReason': 'Reason for suspension',
  'tenant.reasonPrivate': 'Only operators see this.',
  'tenant.notFound': 'No such organisation.',
  'tenant.boundary.title': 'You see it from outside',
  'tenant.tabs': 'Organisation views',
  'tenant.contact': 'Contact person',
  'tenant.contact.since': 'Signed the organisation up on {date}',
  'tenant.lastRequest': 'Last request',
  'tenant.lastRequest.never': 'Never',
  'tenant.ago.minutes': '{count} min ago',
  'tenant.ago.hours': '{count} h ago',
  'tenant.ago.days': '{count} d ago',
  'tenant.seats.unlimited': 'Unlimited',
  'tenant.tab.overview': 'Overview',
  'tenant.tab.plan': 'Plan and seats',
  'tenant.tab.features': 'Features',
  'tenant.tab.audit': 'Audit log',
  'tenant.boundary.visible': 'You see',
  'tenant.boundary.hidden': 'You do not see',
  'tenant.boundary.v1': 'Identifier, state, creation',
  'tenant.boundary.v2': 'Plan, seats, features',
  'tenant.boundary.v3': 'The contact’s name and email',
  'tenant.boundary.h1': 'Projects and tasks',
  'tenant.boundary.h2': 'Comments and attachments',
  'tenant.boundary.h3': 'Time, contracts, email',
  'tenant.boundary.enforced':
    'The database enforces it: the operator Postgres role has no grant on any table that carries a workspace_id.',

  'tenant.suspend': 'Suspend',
  'tenant.suspend.title': 'Suspend {name}',
  'tenant.suspend.lede':
    '{people} people are locked out of all {workspaces} workspaces at once. Data stays untouched; activating gives access back.',
  'tenant.suspend.reason': 'Reason',
  'tenant.suspend.reasonHint': 'Goes into the audit log. At least 10 characters.',
  'tenant.suspend.confirm': 'Suspend',
  'tenant.suspend.working': 'Suspending…',
  'tenant.suspend.cancel': 'Cancel',
  'tenant.activate': 'Activate',
  'tenant.activate.working': 'Activating…',

  'plans.heading': 'Plans',
  'plans.lede': 'A plan sets how many seats a tenant has and which features it gets.',
  'plans.column.name': 'Name',
  'plans.column.price': 'Price / seat / month',
  'plans.column.seats': 'Seat limit',
  'plans.column.features': 'Features',
  'plans.column.tenants': 'Tenants',
  'plans.column.state': 'State',
  'plans.empty': 'No plans yet. Until the first one, every tenant grows without a cap.',
  'plans.loading': 'Loading…',
  'plans.failed': 'The plans did not load.',
  'plans.retry': 'Try again',
  'plans.new': 'New plan',
  'plans.edit': 'Edit',
  'plans.unlimited': 'Unlimited',
  'plans.state.active': 'Offered',
  'plans.state.inactive': 'Withdrawn',
  'plans.tenantsOn': 'tenants on it: {count}',

  'plans.form.create': 'New plan',
  'plans.form.edit': 'Edit plan',
  'plans.form.name': 'Name',
  'plans.form.price': 'Price per seat, per month',
  'plans.form.priceHint': 'A number, with at most two decimals.',
  'plans.form.currency': 'Currency',
  'plans.form.currencyHint': '3-letter code, such as EUR or HUF.',
  'plans.form.seats': 'Seats',
  'plans.form.seatsHint': 'Empty means unlimited. 0 means nobody may be added.',
  'plans.form.features': 'Features',
  'plans.form.active': 'Offered',
  'plans.form.activeHint': 'Withdrawing keeps existing tenants on it; new ones cannot get it.',
  'plans.form.save': 'Save',
  'plans.form.saving': 'Saving…',
  'plans.form.cancel': 'Cancel',

  'tenant.plan': 'Plan',
  'tenant.plan.none': 'No plan',
  'tenant.plan.noneHint': 'With no plan there is no seat limit. That is the ordinary trial state.',
  'tenant.plan.change': 'Change plan',
  'tenant.plan.saving': 'Saving…',
  'tenant.plan.inactive': 'withdrawn',

  'tenant.seats': 'Seats',
  'tenant.seats.used': 'In use',
  'tenant.seats.pending': '{count} pending invitations',
  'tenant.seats.pendingHint': 'An invitation nobody has accepted holds a seat too.',
  'tenant.seats.limit': 'Limit',
  'tenant.seats.override': 'Tenant override',
  'tenant.seats.overrideHint': "Leave empty to follow the plan's limit.",
  'tenant.seats.fromPlan': 'From the plan',
  'tenant.seats.save': 'Save limit',
  'tenant.seats.saving': 'Saving…',
  'tenant.seats.over':
    'More seats are in use than the limit allows. Nobody was removed: the tenant simply cannot grow until it is back under the limit.',

  'tenant.features': 'Features',
  'tenant.features.lede':
    'The plan sets the baseline; here one tenant can be given something different.',
  'tenant.features.on': 'On',
  'tenant.features.off': 'Off',
  'tenant.features.source.plan': 'From the plan',
  'tenant.features.source.override': 'Tenant override',
  'tenant.features.source.default': 'Default',
  'tenant.features.clear': 'Clear the override',
  'tenant.features.clearHint': 'Clearing it gives: {value}',
  'tenant.features.saving': 'Saving…',
  'tenant.features.failed': 'The features did not load.',

  'error.unauthenticated': 'The session has ended. Sign in again.',
  'error.not_found': 'That organisation is gone. It may have been removed.',
  'error.already_suspended': 'This organisation is already suspended.',
  'error.already_active': 'This organisation is already active.',
  'error.validation_failed': 'Those details are not valid.',
  'error.duplicate_name': 'A plan with that name already exists.',
  'error.unknown_feature': 'No deploy registers that feature.',
  'error.unknown_plan': 'No such plan.',
  'error.already_default': 'This tenant has no override to clear.',
  'error.conflict': 'Somebody else changed this. Reload and try again.',
  'error.unknown': 'Something went wrong. Try again.',
  'error.unreachable': 'The server is not responding.',

  /* ── S7 ─────────────────────────────────────────────────────────────────────────────────── */

  'title.operations': 'Operations · Control Panel',
  'title.audit': 'Audit log · Control Panel',
  'nav.operations': 'Background work',
  'nav.audit': 'Audit log',

  /* ── C27.5 · what the agent has cost. See the Hungarian side for the argument. ───────────── */
  'title.spend': 'Agent · Control Panel',
  'nav.spend': 'Agent usage',
  'spend.heading': 'Agent usage',
  'spend.lede':
    'How many tokens the agent has used, per workspace. Counts only: the contents of a conversation cannot reach this screen, and there is no field for one.',
  'spend.window.label': 'Window',
  'spend.window.today': 'Today',
  'spend.window.week': '7 days',
  'spend.window.month': '30 days',
  'spend.window.quarter': '90 days',
  'spend.loading': 'Loading…',
  'spend.failed': 'Agent usage did not load.',
  'spend.retry': 'Retry',
  'spend.empty': 'Nobody asked the agent in this window.',
  'spend.cap': 'Daily ceiling per workspace: {cap} tokens. Data from {since}.',
  'spend.noCap': 'No daily ceiling is configured.',
  'spend.column.tenant': 'Tenant / workspace',
  'spend.column.workspace': 'Workspace',
  'spend.column.questions': 'Questions',
  'spend.column.calls': 'API calls',
  'spend.column.billed': 'Billed tokens',
  'spend.column.cached': 'From cache',
  'spend.column.where': 'Processed in',
  'spend.column.last': 'Last question',

  /* ── C51.4 · what the programs did. See the Hungarian side for the argument. ─────────────── */
  /* ⚠️ *Confirmed* writes, not *"writes MCP made"* — the latter claims something that cannot happen. */

  'audit.heading': 'Operator audit log',
  'audit.lede':
    'Every action taken from the Control Panel or by the clock — who, what, when, and what changed.',
  'audit.loading': 'Loading the log…',
  'audit.failed': 'The log did not load.',
  'audit.retry': 'Try again',
  'audit.empty': 'Nothing has been done on this installation yet.',
  'audit.emptyFiltered': 'No entries match this filter.',
  'audit.more': 'Older',
  'audit.noDiff': 'No change recorded.',
  'audit.removed': 'cleared',
  'audit.filter.resource': 'By resource',
  'audit.filter.action': 'By action',
  'audit.filter.allResources': 'All resources',
  'audit.filter.allActions': 'All actions',
  'audit.column.when': 'When',
  'audit.column.actor': 'Who',
  'audit.column.action': 'Action',
  'audit.column.resource': 'Resource',
  'audit.column.change': 'Change',
  'audit.actor.system': 'automatic',
  'audit.actor.systemBadge': 'automatic',
  'audit.actor.unknown': 'Unknown operator',
  'audit.action.tenantSuspend': 'Organisation suspended',
  'audit.action.tenantActivate': 'Organisation activated',
  'audit.action.tenantPlanAssign': 'Plan assigned',
  'audit.action.tenantSeatLimitSet': 'Seat limit changed',
  'audit.action.tenantFlagSet': 'Feature overridden',
  'audit.action.tenantFlagClear': 'Feature override cleared',
  'audit.action.planCreate': 'Plan created',
  'audit.action.planUpdate': 'Plan updated',
  'audit.action.planDeactivate': 'Plan withdrawn',
  'audit.action.jobRequeue': 'Job sent back to the queue',
  'audit.action.choreRun': 'Scheduled work run by hand',
  'audit.resource.tenant': 'Organisation',
  'audit.resource.plan': 'Plan',
  'audit.resource.job': 'Job',
  'audit.resource.chore': 'Scheduled work',

  'ops.payloadNote':
    'You cannot see job payloads, such as an email’s recipient and body. You may re-run work, never read it.',
  'ops.jobsEmpty':
    'No jobs here. Anything that gets stuck shows up here, and you can send it back to the queue from here.',
  'ops.chores.runBody':
    'It acts on live data and may send email to customers. The schedule is unchanged: the next run comes at its usual time.',
  'enrol.step.begin': 'Begin',
  'enrol.step.key': 'Key',
  'enrol.step.verify': 'Verify',
  'enrol.step.done': 'Done',
  'enrol.steps': 'Enrolment steps',
  'ops.heading': 'Operations',
  'ops.lede':
    'The work the clock starts, and the queue. A failure here is reported by nothing else.',
  'ops.loading': 'Loading…',
  'ops.failed': 'This did not load.',
  'ops.retry': 'Try again',
  'ops.never': 'Never run',
  'ops.cadence.minutes': 'every {n} min',
  'ops.cadence.hours': 'every {n} h',
  'ops.cadence.days': 'every {n} d',
  'ops.chores.heading': 'Scheduled work',
  'ops.chores.lede':
    'Started by the clock, not by a request. Each one is safe to repeat, so it can be run by hand.',
  'ops.chores.column.chore': 'Work',
  'ops.chores.column.cadence': 'Cadence',
  'ops.chores.column.lastRun': 'Last run',
  'ops.chores.column.state': 'State',
  'ops.chores.column.outcome': 'Outcome',
  'ops.chores.column.day': 'Failed / 24h',
  'ops.chores.run': 'Run now',
  'ops.chores.running': 'Running…',
  'ops.chores.overdue': 'Has missed two cadences.',
  'ops.chores.nothingToDo': 'Nothing to do.',
  'ops.chores.confirm':
    'Run this now? It works on live data — it may send email or suspend an organisation.',
  'ops.chore.running': 'Running',
  'ops.chore.succeeded': 'OK',
  'ops.chore.failed': 'Failed',
  'ops.queue.heading': 'Queue',
  'ops.queue.lede': 'What a request asked for, rolled up by kind.',
  'ops.queue.column.kind': 'Kind',
  'ops.queue.column.pending': 'Pending',
  'ops.queue.column.running': 'Running',
  'ops.queue.column.done': 'Done',
  'ops.queue.column.failed': 'Failed',
  'ops.queue.column.oldest': 'Oldest pending',
  'ops.queue.unhandled': 'no handler',
  'ops.jobs.heading': 'Background jobs',
  'ops.jobs.lede': 'What has spent its attempts, and what a dead worker is still holding.',
  'ops.jobs.empty': 'No failed jobs. The queue is clean.',
  'ops.jobs.column.kind': 'Kind',
  'ops.jobs.column.tenant': 'Organisation',
  'ops.jobs.column.attempts': 'Attempts',
  'ops.jobs.column.error': 'Error',
  'ops.jobs.column.when': 'Created',
  'ops.jobs.requeue': 'Requeue',
  'ops.jobs.requeueing': 'Sending…',
  'ops.jobs.more': 'Older',
  'ops.jobs.stalled': 'Stalled',
  'ops.jobs.noTenant': 'No organisation',
  'ops.jobs.confirm':
    'Send this back to the queue? The email really goes out, and the attempt count resets to zero.',
  'ops.error.jobNotFailed': 'Only a failed job can be requeued. Reload the list.',
  'ops.error.unknownChore': 'No deploy registers that scheduled work.',

  'tenant.audit': 'Audit log',
  'tenant.auditLede': 'Recent operator actions',
  'tenant.auditEmpty': 'Nobody has done anything to this organisation yet.',
  'tenant.auditAll': 'Full audit log',
  'tenant.auditFailed': 'The log did not load.',

  /* ── C9 · the danger tab and the deletion ────────────────────────────────── */
  'tenant.tab.danger': 'Permanent deletion',
  'danger.tier': 'Irreversible',
  'danger.title': 'Delete this organisation permanently',
  'danger.body':
    'The organisation and everything under it is removed for good. It need not be suspended first, so confirmation takes several steps and is made with the numbers in view.',
  'danger.prepare': 'Prepare deletion',
  'danger.status': 'Status',
  'danger.monthly': 'Monthly value',

  'title.tenantDelete': 'Permanent deletion · Control Panel',
  'delete.back': 'Back to the organisation',
  'delete.title': 'Permanently delete {name}',
  'delete.lede': 'It cannot be undone and cannot be restored from the Control Panel.',
  'delete.step1': 'What it removes',
  'delete.step2': 'Acknowledge',
  'delete.step3': 'Confirm',
  'delete.live': 'Live customer. Not suspended: its members are using it now.',
  'delete.livePaying':
    'Live, paying customer at {amount} a month. Not suspended: its members are using it now.',
  'delete.suspendInstead': 'Suspend instead',
  'delete.ack.workspaces':
    '{count} workspaces are removed, with every project, task, time entry and contract inside them.',
  'delete.ack.people': '{count} people lose access at once.',
  'delete.ack.peopleLive':
    '{count} people lose access at once, even if they are working in it now.',
  'delete.ack.paying':
    'Paying customer at {amount} a month. Deleting does not end the contract or settle billing.',
  'delete.ack.undo': 'I understand this cannot be undone.',
  'delete.slugLabel': 'Type the slug: {slug}',
  'delete.slugHint': 'Pasting is disabled here; type it.',
  'delete.reason': 'Reason',
  'delete.reasonHint': 'Goes into the audit log. At least 10 characters.',
  'delete.missing': 'Still missing:',
  'delete.missing.acks': 'acknowledgements',
  'delete.missing.slug': 'slug',
  'delete.missing.reason': 'reason',
  'delete.ready': 'All conditions are met.',
  'delete.confirm': 'Delete organisation permanently',
  'delete.working': 'Deleting…',
  'delete.cancel': 'Cancel',
  'delete.why.title': 'Why so many steps?',
  'delete.why.body':
    'A slug can be copied off the screen, so typing it is not enough on its own. The acknowledgements prove you saw the numbers, and the reason is the only part of this that still exists in six months.',
  'delete.error.slug': 'That is not this organisation’s slug.',

  'delete.done.kicker': 'Organisation permanently deleted',
  'delete.done.title': '{name} deleted',
  'delete.done.body':
    'The organisation and all its data are gone. The audit log keeps who deleted it, when and why.',
  'delete.done.removed': 'Removed',
  'delete.done.removedValue': '{workspaces} workspaces, {people} people',
  'delete.done.by': 'Deleted by',
  'delete.done.at': 'When',
  'delete.done.reason': 'Reason',
  'delete.done.audit': 'Audit entry',
  'delete.done.toTenants': 'Back to organisations',
  'delete.done.openAudit': 'Open in audit log',

  /* ── C8 · the operator roster ────────────────────────────────────────── */
  'title.operators': 'Operators · Control Panel',
  'nav.operators': 'Operators',
  'operators.heading': 'Operators',
  'operators.lede':
    'Operators manage each other. Signing in requires an enrolled second factor, so an invitee stays “Not yet enrolled” until they do it.',
  'operators.loading': 'Loading…',
  'operators.failed': 'The operator list did not load.',
  'operators.you': 'you',
  'operators.never': 'Never signed in',
  'operators.working': 'Working…',
  'operators.cancel': 'Cancel',
  'operators.column.person': 'Name',
  'operators.column.state': 'State',
  'operators.column.lastSeen': 'Last sign-in',
  'operators.column.since': 'Added',
  'operators.column.actions': 'Actions',
  'operators.state.active': 'Active',
  'operators.state.invited': 'Not yet enrolled',
  'operators.state.invitedHint': 'No authenticator set up yet, so they cannot sign in.',
  'operators.state.suspended': 'Suspended',
  'operators.invite': 'Invite operator',
  'operators.invite.title': 'Invite operator',
  'operators.invite.lede':
    'The invitee sets a password and enrols a second factor from the link, and cannot sign in until then.',
  'operators.invite.email': 'Email',
  'operators.invite.name': 'Name',
  'operators.invite.whatHappens':
    'We email them a link. On it they choose a password, scan the key with an authenticator app and finish with its first code — which signs them straight in. The link works for 3 days.',
  'operators.invite.confirm': 'Send the invitation',
  'operators.invite.working': 'Sending…',
  'operators.action.reset': 'Send password reset',
  'operators.action.resend': 'Resend invitation',
  'operators.action.suspend': 'Suspend',
  'operators.action.activate': 'Reactivate',
  'operators.action.delete': 'Delete',
  'operators.suspend.title': 'Suspend {who}',
  'operators.suspend.lede':
    'Signs them out at once; they cannot sign in until reactivated. Their account and audit entries stay.',
  'operators.suspend.confirm': 'Suspend',
  'operators.delete.title': 'Delete {who}',
  'operators.delete.lede': 'The account is removed for good. Their audit entries keep their name.',
  'operators.delete.confirm': 'Delete',
  'operators.reset.title': 'Send a password reset',
  'operators.reset.lede':
    'We email {who} a link to set a new password. Their authenticator is not affected.',
  'operators.reset.confirm': 'Send it',
  'operators.done.invited': 'Operator invited',
  'operators.done.suspended': 'Operator suspended',
  'operators.done.activated': 'Operator reactivated',
  'operators.done.deleted': 'Operator deleted',
  'operators.done.reset': 'Password reset sent',
  'operators.error.thatIsYou': 'You cannot do this to your own account.',
  'operators.error.lastOperator':
    'They are the last operator who can sign in. Invite somebody else first.',
  'operators.error.alreadyAnOperator': 'That address already belongs to an operator.',
  'operators.error.belongsToAMember':
    'That address belongs to a workspace member. One address cannot be both.',
  'audit.action.tenantDelete': 'Organisation deleted',
  'audit.action.workspaceDeleted': 'Workspace deleted for good',
  'audit.action.operatorInvite': 'Operator invited',
  'audit.action.operatorSuspend': 'Operator suspended',
  'audit.action.operatorActivate': 'Operator restored',
  'audit.action.operatorDelete': 'Operator removed',
  'audit.action.operatorPasswordReset': 'Password reset sent',
  'audit.resource.operator': 'Operator',
  'audit.resource.workspace': 'Workspace',

  /* ── C10 · an organisation is invited ─────────────────────────────────── */
  'status.awaitingSignup': 'Invited',
  'status.awaitingSetup': 'Awaiting setup',
  'tenants.invite': 'Invite organisation',
  'tenants.invite.title': 'Invite organisation',
  'tenants.invite.lede':
    'The admin gets a sign-up link by email. The organisation exists from now on, waiting for that sign-up.',
  'tenants.invite.name': 'Organisation name',
  'tenants.invite.nameHint': 'Its address and its first workspace are both built from this.',
  'tenants.invite.adminName': 'Admin name',
  'tenants.invite.adminEmail': 'Admin email',
  'tenants.invite.emailHint':
    'The invitation can only be accepted with this address. Signing up with another one will not work.',
  'tenants.invite.confirm': 'Send invitation',
  'tenants.invite.working': 'Sending…',
  'tenants.invite.cancel': 'Cancel',
  'tenants.invite.done': 'The invitation is on its way to {email}',
  'tenants.invite.error.unsluggable':
    'That name has too few letters or digits to build an address from. Use at least three.',
  'tenants.invite.error.alreadyMember':
    'That address already belongs to a workspace member. One address can start only one organisation.',
  'tenants.invite.error.isAnOperator': 'That address belongs to an operator.',

  'tenant.pending.title': 'Invited',
  'tenant.pending.lede':
    'The organisation exists, but nobody has accepted the invitation yet. Its first workspace is ready and has no members.',
  'tenant.pending.address': 'Invited:',
  'tenant.pending.expires': 'Link expires:',
  'tenant.pending.expired': 'Link expired:',
  'tenant.pending.resend': 'Resend invitation',
  'tenant.pending.working': 'Sending…',
  'tenant.pending.resendHint': 'Resending issues a new link; the old one stops working at once.',
  'tenant.pending.sent': 'Invitation resent',
  'tenant.pending.failed': 'The invitation could not be sent again.',
  'audit.action.tenantInvite': 'Organisation invited',
  'audit.action.tenantInviteResend': 'Invitation resent',
  'tenants.from': 'Created from',
  'tenants.to': 'to',
  'tenants.clearFilters': 'Clear filters',
  'tenants.emptyFilter': 'Your filters hide every organisation.',
  'tenants.withdrawn': 'withdrawn',
  'tenants.invite.emailPlaceholder': 'name@company.com',
  'tenants.invite.plan': 'Plan',
  'tenants.invite.seats': 'Seats',
  'tenants.invite.seatsFromPlan': 'Empty uses the plan limit: {limit}.',
  'tenants.invite.seatsOverride': 'Override: {count} seats.',
  'tenants.invite.unlimited': 'unlimited',
  'tenants.invite.done.title': 'Invitation sent',
  'pager.page': 'Page {n}',
  'pager.previous': 'Previous page',
  'pager.next': 'Next page',
  'pager.jump': 'Go to page',
  'cal.today': 'Today',
  'cal.clear': 'Clear',
  'cal.previous': 'Previous',
  'cal.next': 'Next',
  'health.missed': '{count} cadences missed',
  'toast.region': 'Notifications',
  'toast.dismiss': 'Dismiss',
  'stepper.decrement': 'Decrease',
  'stepper.increment': 'Increase',
  'tenant.createdOn': 'Created {date}',
  'tenant.planLine': '{plan} · {price} / seat / month',
  'tenant.suspendedSince': 'Suspended since {date}. Every member is locked out.',
  'tenant.contact.none': 'This organisation has nobody to write to yet.',
  'tenant.contact.resetSent': 'Password reset sent',
  'tenant.suspend.tier': 'Locks members out',
  'tenant.suspend.short': '{count} more characters',
  'tenant.suspend.done': 'Organisation suspended',
  'tenant.activate.title': 'Activate {name}',
  'tenant.activate.lede':
    'Members can work again from their next request. Nothing was touched while it was suspended.',
  'tenant.activate.done': 'Organisation activated',
  'tenant.seats.overShort': 'Over the limit',
  'tenant.seats.fromOverride': 'Tenant override',
  'tenant.trialUntil': 'Trial until {date}',
  'tenant.notBilling': 'Suspended, not billing',
  'tenant.overrides': 'Overridden features',
  'tenant.overrides.none': 'None; everything is inherited.',
  'feature.on': 'On',
  'feature.off': 'Off',
  'when.today': 'today',
  'when.yesterday': 'yesterday',
  'tenant.beyond.title': 'Projects, tasks, time',
  'tenant.beyond.sub': 'Beyond the boundary. Nobody sees this figure from here.',
  'tenant.contact.never': 'never signed in',
  'tenant.contact.lastSeen': 'last sign-in {when}',
  'tenant.contact.reset': 'Send password reset',
  'tenant.pending.expiresOn': 'The link expires {date}',
  'tenant.pending.expiredOn': 'The link expired {date}',
  'tenant.pending.setupTitle': 'Awaiting setup',
  'tenant.pending.acceptedOn':
    'Accepted {date} — no workspace yet. A resent link takes them to sign in and finish setting up.',
  'tenant.adjust': 'Adjust',
  'tenant.plan.effect': 'New monthly value: {value} · limit: {limit} · features: {features}',
  'tenant.plan.limitOverride': '{count} (override)',
  'tenant.plan.current': '{price} / seat / month · {features}',
  'tenant.save': 'Save',
  'tenant.cancel': 'Cancel',
  'tenant.plan.withdrawnNote':
    'This plan is no longer offered to new organisations; this one may stay on it.',
  'tenant.seats.overCount':
    '{used} seats used against a limit of {limit}. Nobody new can be added until you raise the limit or {free} seats free up. Nobody was removed.',
  'tenant.seats.pendingAfter':
    '{count} pending invitations. If all accept, {after} seats are used.',
  'tenant.seats.pendingBeyond':
    '{count} pending invitations. If all accept, {after} seats are used, which is over the limit; invitations past the limit cannot be accepted.',
  'tenant.seats.hint': 'Empty uses the plan limit ({limit}). 0 means nobody may be added.',
  'tenant.seats.clear': 'Clear the override',
  'tenant.features.column.name': 'Feature',
  'tenant.features.column.value': 'Value',
  'tenant.features.column.source': 'Source',
  'tenant.features.after': 'After clearing: {value}',
  'feature.reports': 'Reports',
  'feature.lineItemPricing': 'Line-item pricing',
  'feature.workFreeRevenue': 'Work-free revenue',
  'feature.agent': 'Agent',
  'feature.mcp': 'MCP access',
  'tenant.boundary.body':
    'As an operator you manage the frame of an organisation: plan, seats, features and state. What is inside the workspaces belongs to the customer.',
  'tenant.boundary.v4': 'Usage totals',
  'tenant.boundary.h4': 'Background job contents',
  'audit.action.tenantContactReset': 'Password reset sent to the contact',
  'plans.created': 'Plan created',
  'plans.saved': 'Plan saved',
  'plans.form.nameRequired': 'Give the plan a name.',
  'plans.form.nobody': 'Nobody may be added',
  'plans.form.seatsCount': '{count} seats',
  'plans.form.withdrawWarn': '{count} tenants stay on it.',
  'ops.chores.late': '{count} overdue',
  'ops.chores.onCadence': 'Everything runs on cadence',
  'ops.chores.lateShort': 'Overdue',
  'ops.chores.lateMissed': 'Overdue · missed {count} cadences',
  'ops.chores.started': 'Scheduled work started',
  'ops.chores.runTitle': 'Run now: {name}',
  'ops.chores.tier': 'On live data',
  'ops.chores.runConfirm': 'Run',
  'ops.cadence.hourly': 'Hourly',
  'ops.cadence.daily': 'Daily',
  'ops.jobs.summary': '{count} failed, waiting for a requeue',
  'ops.jobs.summaryNone': 'No failed jobs',
  'ops.jobs.filter': 'By state',
  'ops.jobs.filter.all': 'All states',
  'ops.jobs.state.failed': 'Failed',
  'ops.jobs.state.pending': 'Queued',
  'ops.jobs.state.running': 'Running',
  'ops.jobs.state.done': 'Done',
  'ops.jobs.column.state': 'State',
  'ops.jobs.requeued': 'Job sent back to the queue',
  'ops.jobs.requeueTitle': 'Send the job back to the queue',
  'ops.jobs.tier': 'May send real email',
  'ops.jobs.count': '{count} jobs',
  'chore.alertScan': 'Project risk scan',
  'chore.dueSweep': 'Due-date reminders',
  'chore.dueSweepPrune': 'Reminder ledger cleanup',
  'chore.leaveCarryOver': 'Leave carry-over',
  'chore.trialExpiry': 'Trial expiry check',
  'chore.integrationOverwatch': 'Tracker reconciliation',
  'chore.integrationOutbound': 'Outbound tracker changes',
  'chore.integrationPoll': 'Tracker change poll',
  'chore.blobSweep': 'Orphan file sweep',
  'spend.window': 'Window',
  'spend.window.7': '7 days',
  'spend.window.30': '30 days',
  'spend.window.90': '90 days',
  'spend.note':
    'Daily ceiling per workspace: {cap} billed tokens. At the ceiling the agent stops answering until the next UTC day.',
  'spend.column.tokens': 'Billed tokens',
  'spend.column.ceiling': 'Today’s ceiling',
  'spend.atCeiling': 'At ceiling',
  'audit.filter.clearResource': 'Clear filter',
  'audit.count': '{count} entries',
  'operators.column.factor': 'Second factor',
  'operators.factor.enrolled': 'Enrolled',
  'operators.factor.enrolledOn': 'Enrolled {date}',
  'operators.factor.none': 'Not enrolled',
  'operators.selfNote': 'You cannot suspend or delete yourself.',
  'operators.done.resent': 'Invitation resent',
  'operators.invite.emailPlaceholder': 'name@loginet.hu',
  'operators.suspend.tier': 'Locks them out',
  'operators.delete.type': 'Type their email: {email}',
  'signIn.continue': 'Continue',
  'signIn.codeShape': 'The code has 6 digits.',
  'signIn.back': 'Back',
  'enrol.copy': 'Copy',
  'enrol.copied': 'Copied',
  'enrol.secretNote':
    'The key is shown only now. If you lose your phone, the factor is reset from the command line by whoever runs the installation.',
  'enrol.verifyLede': 'Enter the 6-digit code the app shows now.',
  'enrol.doneTitle': 'Enrolled',
  'dialog.close': 'Close',
  'title.join': 'Sign up · Control Panel',
  'join.title': 'Join the Control Panel',
  'join.lede':
    'You have been invited to operate the Forecastify Control Panel. Choose a password, then set up an authenticator app — signing up finishes with its first code.',
  'join.as': 'Invited address',
  'join.step.password': 'Password',
  'join.password': 'Password',
  'join.confirm': 'Password again',
  'join.rules': 'Password rules',
  'join.rule.length': 'At least {min} characters',
  'join.rule.letters': 'Both lower- and upper-case letters',
  'join.rule.digits': 'At least one digit',
  'join.rule.match': 'Both passwords match',
  'join.begin': 'Continue to the key',
  'join.beginWorking': 'Saving…',
  'join.weak': 'That password does not meet the rules.',
  'join.finish': 'Finish signing up',
  'join.finishWorking': 'Finishing…',
  'join.wrongCode': 'That code did not work. Enter the one your app shows now.',
  'join.loading': 'Loading the invitation…',
  'join.dead.title': 'This invitation is no longer valid',
  'join.dead.lede':
    'It has expired, been used, or been replaced by a newer one. Ask another operator to send you a new invitation.',
  'join.welcome': 'Welcome to the Control Panel',
  'join.welcomeBody': 'Your second factor is set up; every sign-in will ask for it.',
  'audit.action.operatorInviteResend': 'Operator invitation resent',
  'auth.label': 'CONTROL PANEL',
  'auth.footer.company': 'LogiNet Systems Kft.',
  'signIn.loggingInAs': 'Signing in as',
  'signIn.changeEmail': 'Change',
  'chore.emailAvatars': 'Email profile pictures',
  'chore.requestMail': 'Leave and overtime reminders',
  'chore.contractWatch': 'Contract watch',
  'chore.alertDigest': 'Daily project alerts',
  'chore.inviteReminder': 'Invitation reminders',
  'chore.loggingMail': 'Time-logging reminders',
  'chore.financeMail': 'Billable items',
  'chore.integrationMail': 'Sync failure alerts',
};

export const CATALOGUES = { hu: HU as Catalogue, en: EN } as const;
export type Locale = keyof typeof CATALOGUES;
export const LOCALES: readonly Locale[] = ['hu', 'en'];
